import { useNavigate } from "react-router-dom";
import { Building2, MailOpen, LifeBuoy } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/context/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Full-page state for authenticated users without an organization:
 * pending invite → accept CTA; provisioning failure → contact support;
 * otherwise (e.g. removed from an org) → ask for a new invitation.
 */
export function NoOrganizationState() {
  const { pendingInvitation, provisioningFailed } = useOrganization();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  let icon = <Building2 className="h-12 w-12 text-muted-foreground" />;
  let title = "You're not part of an organization";
  let description =
    "Your account is not currently a member of any organization. Ask an organization admin to send you an invitation, then open the link from the invitation email.";
  let action: React.ReactNode = null;

  if (pendingInvitation) {
    icon = <MailOpen className="h-12 w-12 text-purple-500" />;
    title = `You've been invited to ${pendingInvitation.organizationName}`;
    description = `${pendingInvitation.inviterName} invited you to join ${pendingInvitation.organizationName}. Accept the invitation to get access to the workspace.`;
    action = (
      <Button
        className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
        onClick={() => navigate(`/accept-invitation/${pendingInvitation.id}`)}
      >
        Review invitation
      </Button>
    );
  } else if (provisioningFailed) {
    icon = <LifeBuoy className="h-12 w-12 text-amber-500" />;
    title = "Your workspace is not ready yet";
    description =
      "Something went wrong while setting up your workspace during sign-up. Please contact support at support@youzu.ai and we'll fix it right away.";
  }

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <Card className="max-w-lg">
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          {icon}
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
          <div className="flex items-center gap-3 pt-2">
            {action}
            <Button variant="outline" onClick={() => signOut()}>
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
